import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import RefreshIcon from '@material-ui/icons/Refresh';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import useBgvTrackingStyles from './useBgvTrackingStyles';
import {
    BgvBatch,
    BgvBucket,
    BgvPreview,
    BgvRider,
    backfillByFile,
    fetchBatches,
    fetchBatchReport,
    fetchBgvPrefill,
    fetchRiderSheet,
    fetchCounts,
    fetchHubs,
    fetchRiders,
    overrideBgv,
    retriggerBgv,
    BgvOverrideStatus,
    BgvPrefill,
} from './bgvApi';
import { getSingleStaff } from '../../store/actions/staffActionCreator';
import RetriggerBGVModal from '../../components/Modals/RetriggerBGVModal';
import OverrideBgvModal from './OverrideBgvModal';
import { getPermissions, showMessage } from '../../utils/helper';

const BUCKETS: { key: BgvBucket; label: string; color: string }[] = [
    { key: 'CLEAR', label: 'Clear', color: '#059669' },
    { key: 'DISCREPANT', label: 'Discrepant', color: '#DC2626' },
    { key: 'ATTENTION_REQUIRED', label: 'Attention Required', color: '#C2410C' },
    { key: 'INSUFFICIENT', label: 'Insufficient', color: '#D97706' },
    { key: 'IN_PROGRESS', label: 'In Progress', color: '#2563EB' },
];
const BUCKET_BY_KEY = Object.fromEntries(BUCKETS.map((b) => [b.key, b])) as Record<BgvBucket, typeof BUCKETS[0]>;
const MAX_RANGE_DAYS = 31;
const BATCH_PAGE_SIZE = 10;
const fmt = (d: string) => (d ? moment(d).format('DD MMM YY, hh:mm A') : '—');

const BgvTrackingPage: React.FC = () => {
    const classes = useBgvTrackingStyles();
    const [tab, setTab] = useState(0);
    const orgId = useSelector((state: any) => state?.settings?.basicDetails?.id || state?.user?.orgId);
    const [hubs, setHubs] = useState<{ id: number; name: string }[]>([]);
    // USRPM: read shows a tab, update allows its actions.
    const tracking = getPermissions('BGV TRACKING');
    const backfilling = getPermissions('BGV BACKFILLING');
    const tabs = [
        { key: 'tracking', label: 'Tracking', readable: !!tracking?.read },
        { key: 'backfill', label: 'Backfill', readable: !!backfilling?.read },
    ].filter((t) => t.readable);

    useEffect(() => {
        if (orgId) fetchHubs(orgId).then(setHubs).catch(() => setHubs([]));
    }, [orgId]);

    return (
        <Paper className={classes.container} elevation={0}>
            <Box className={classes.header}>
                <Typography className={classes.title}>Background Verification</Typography>
                <Typography className={classes.subTitle}>
                    Amazon Now riders · each rider counted once, by their latest case (overrides included)
                </Typography>
            </Box>
            {tabs.length === 0 ? (
                <Typography className={classes.muted} style={{ padding: 24 }}>
                    You do not have access to background verification.
                </Typography>
            ) : (
                <>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary" className={classes.tabs}>
                        {tabs.map((t) => <Tab key={t.key} label={t.label} />)}
                    </Tabs>
                    {tabs[tab]?.key === 'backfill'
                        ? <BackfillTab hubs={hubs} canWrite={!!backfilling?.update} />
                        : <TrackingTab hubs={hubs} canWrite={!!tracking?.update} />}
                </>
            )}
        </Paper>
    );
};

const TrackingTab: React.FC<{ hubs: { id: number; name: string }[]; canWrite: boolean }> = ({ hubs, canWrite }) => {
    const classes = useBgvTrackingStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    const [startDate, setStartDate] = useState<Date | null>(moment().subtract(29, 'days').toDate());
    const [endDate, setEndDate] = useState<Date | null>(new Date());
    const [hubId, setHubId] = useState<number | ''>('');
    const [bucket, setBucket] = useState<BgvBucket | ''>('');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [counts, setCounts] = useState<Record<BgvBucket, number> | null>(null);
    const [total, setTotal] = useState(0);
    const [riders, setRiders] = useState<BgvRider[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);

    const rangeError =
        startDate && endDate && moment(endDate).diff(moment(startDate), 'days') >= MAX_RANGE_DAYS
            ? `Pick a range of at most ${MAX_RANGE_DAYS} days`
            : null;

    const filters = {
        startTime: moment(startDate).startOf('day').valueOf(),
        endTime: moment(endDate).endOf('day').valueOf(),
        hubId,
    };

    const load = useCallback(async () => {
        if (!startDate || !endDate || rangeError) return;
        setLoading(true);
        try {
            const [c, r] = await Promise.all([fetchCounts(filters), fetchRiders(filters, bucket, page, size)]);
            setCounts(c.counts);
            setTotal(c.total);
            setRiders(r.riders);
            setTotalElements(r.totalElements);
        } catch (e: any) {
            showMessage(e?.message ?? 'Could not load BGV data', 'error');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, hubId, bucket, page, size, rangeError]);

    useEffect(() => {
        load();
    }, [load]);

    const pickBucket = (b: BgvBucket | '') => {
        setBucket((current) => (current === b ? '' : b));
        setPage(0);
    };

    const openRider = (userId: number) => {
        dispatch(getSingleStaff(userId, () => history.push(`/staff/details/${userId}`)));
    };

    const [overrideRider, setOverrideRider] = useState<BgvRider | null>(null);
    const [retriggerRider, setRetriggerRider] = useState<(BgvRider & BgvPrefill) | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const runAction = async (action: () => Promise<void>, done: string, close: () => void) => {
        setActionLoading(true);
        try {
            await action();
            showMessage(done, 'success');
            close();
            load();
        } catch (e: any) {
            showMessage(e?.message ?? 'Request failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const submitOverride = (status: BgvOverrideStatus, reason: string) =>
        overrideRider &&
        runAction(() => overrideBgv(overrideRider.userId, status, reason), 'BGV status overridden', () => setOverrideRider(null));

    const submitRetrigger = (address: string, fatherName: string) =>
        retriggerRider &&
        runAction(() => retriggerBgv(retriggerRider.userId, address, fatherName), 'BGV retriggered', () => setRetriggerRider(null));

    const openRetrigger = async (r: BgvRider) => {
        setActionLoading(true);
        let prefill: BgvPrefill = { firstName: '', dob: '', fatherName: '', address: '' };
        try {
            prefill = await fetchBgvPrefill(r.userId);
        } catch (e: any) {
            showMessage(e?.message ?? 'Could not load the rider details', 'warning');
        } finally {
            setActionLoading(false);
        }
        setRetriggerRider({ ...r, ...prefill });
    };

    const renderAction = (r: BgvRider) => {
        const stop = (fn: () => void) => (e: React.MouseEvent) => {
            e.stopPropagation();
            fn();
        };
        if (r.bucket === 'ATTENTION_REQUIRED') {
            return (
                <Button size="small" variant="outlined" color="primary" onClick={stop(() => setOverrideRider(r))}>
                    Override
                </Button>
            );
        }
        if (r.bucket === 'INSUFFICIENT' || r.bucket === 'IN_PROGRESS') {
            return (
                <Button size="small" variant="outlined" color="primary" disabled={actionLoading} onClick={stop(() => openRetrigger(r))}>
                    Retrigger BGV
                </Button>
            );
        }
        return null;
    };

    return (
        <>
            <Box className={classes.filters}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                        label="From"
                        value={startDate}
                        onChange={(d) => { setStartDate(d); setPage(0); }}
                        format="dd MMM yyyy"
                        inputVariant="outlined"
                        size="small"
                        maxDate={endDate ?? undefined}
                        InputProps={{ readOnly: true }}
                    />
                    <KeyboardDatePicker
                        label="To"
                        value={endDate}
                        onChange={(d) => { setEndDate(d); setPage(0); }}
                        format="dd MMM yyyy"
                        inputVariant="outlined"
                        size="small"
                        minDate={startDate ?? undefined}
                        disableFuture
                        error={!!rangeError}
                        helperText={rangeError ?? undefined}
                        InputProps={{ readOnly: true }}
                    />
                </MuiPickersUtilsProvider>
                <TextField
                    select
                    label="Hub"
                    variant="outlined"
                    size="small"
                    className={classes.select}
                    value={hubId}
                    onChange={(e) => { setHubId(e.target.value === '' ? '' : Number(e.target.value)); setPage(0); }}
                >
                    <MenuItem value="">All hubs</MenuItem>
                    {hubs.map((h) => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
                </TextField>
                <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
                    Refresh
                </Button>
                {loading && <CircularProgress size={20} />}
            </Box>

            <Box className={classes.cards}>
                <Paper
                    elevation={0}
                    className={`${classes.card} ${bucket === '' ? classes.cardActive : ''}`}
                    onClick={() => pickBucket('')}
                >
                    <Typography className={classes.cardLabel}>All riders</Typography>
                    <Typography className={classes.cardValue}>{counts ? total.toLocaleString() : '—'}</Typography>
                </Paper>
                {BUCKETS.map((b) => (
                    <Paper
                        key={b.key}
                        elevation={0}
                        className={`${classes.card} ${bucket === b.key ? classes.cardActive : ''}`}
                        onClick={() => pickBucket(b.key)}
                    >
                        <Typography className={classes.cardLabel}>
                            <span className={classes.dot} style={{ background: b.color }} />
                            {b.label}
                        </Typography>
                        <Typography className={classes.cardValue}>{counts ? counts[b.key].toLocaleString() : '—'}</Typography>
                    </Paper>
                ))}
            </Box>

            <Box className={classes.tableSection}>
                <TableContainer className={classes.tableContainer}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {['Rider', 'Mobile', 'Hub', 'BGV status', 'Request ID', 'Initiated', 'Last update', 'Report', ...(canWrite ? ['Action'] : [])].map((h) => (
                                    <TableCell key={h} className={classes.headCell}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {riders.map((r) => {
                                const b = BUCKET_BY_KEY[r.bucket];
                                return (
                                    <TableRow key={r.userId} className={classes.row} onClick={() => openRider(r.userId)}>
                                        <TableCell className={classes.cell} style={{ fontWeight: 600 }}>{r.name || '—'}</TableCell>
                                        <TableCell className={classes.cell}>{r.contactNumber || '—'}</TableCell>
                                        <TableCell className={classes.cell}>{r.hubName || '—'}</TableCell>
                                        <TableCell className={classes.cell}>
                                            <Chip
                                                label={b?.label ?? r.bucket}
                                                size="small"
                                                className={classes.chip}
                                                style={{ color: b?.color, background: `${b?.color}14`, border: `1px solid ${b?.color}40` }}
                                            />
                                        </TableCell>
                                        <TableCell className={classes.cell}>{r.requestId || '—'}</TableCell>
                                        <TableCell className={classes.cell}>{fmt(r.initiatedAt)}</TableCell>
                                        <TableCell className={classes.cell}>{fmt(r.updatedAt)}</TableCell>
                                        <TableCell className={classes.cell}>
                                            {r.reportLink ? (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    href={r.reportLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                >
                                                    View report
                                                </Button>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                        {canWrite && <TableCell className={classes.cell}>{renderAction(r)}</TableCell>}
                                    </TableRow>
                                );
                            })}
                            {!loading && riders.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={canWrite ? 9 : 8} align="center" className={classes.muted} style={{ padding: 40 }}>
                                        No riders match these filters
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    rowsPerPage={size}
                    rowsPerPageOptions={[20, 50, 100]}
                    onChangePage={(_: any, p: number) => setPage(p)}
                    onChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) => { setSize(Number(e.target.value)); setPage(0); }}
                />
            </Box>

            {overrideRider && (
                <OverrideBgvModal
                    open
                    loading={actionLoading}
                    name={overrideRider.name}
                    contactNumber={overrideRider.contactNumber}
                    currentStatus={BUCKET_BY_KEY[overrideRider.bucket]?.label ?? overrideRider.bucket}
                    onClose={() => setOverrideRider(null)}
                    onSubmit={submitOverride}
                />
            )}
            {retriggerRider && (
                <RetriggerBGVModal
                    open
                    loading={actionLoading}
                    name={retriggerRider.firstName || retriggerRider.name}
                    contactNumber={retriggerRider.contactNumber}
                    dob={retriggerRider.dob ? moment(retriggerRider.dob).format('DD MMM YYYY') : ''}
                    defaultAddress={retriggerRider.address}
                    defaultFatherName={retriggerRider.fatherName}
                    showFatherName
                    onClose={() => setRetriggerRider(null)}
                    onSubmit={submitRetrigger}
                />
            )}
        </>
    );
};

const PreviewStats: React.FC<{ preview: BgvPreview }> = ({ preview }) => {
    const classes = useBgvTrackingStyles();
    const rows: [string, string][] = [
        ['Riders matched', preview.requested.toLocaleString()],
        ['Already verified or in progress', preview.alreadyVerifiedOrInProgress.toLocaleString()],
        ['Not found', preview.notFound.toLocaleString()],
        ['To initiate', preview.toInitiate.toLocaleString()],
    ];
    return (
        <Box mt={2}>
            {rows.map(([label, value]) => (
                <Box key={label} className={classes.stat}>
                    <span>{label}</span>
                    <span className={classes.statValue}>{value}</span>
                </Box>
            ))}
        </Box>
    );
};

const BackfillTab: React.FC<{ hubs: { id: number; name: string }[]; canWrite: boolean }> = ({ hubs, canWrite }) => {
    const classes = useBgvTrackingStyles();
    const [selectedHubs, setSelectedHubs] = useState<{ id: number; name: string }[]>([]);
    const [file, setFile] = useState<File | null>(null);
    const [filePreview, setFilePreview] = useState<BgvPreview | null>(null);
    const [batches, setBatches] = useState<BgvBatch[]>([]);
    const [batchTotal, setBatchTotal] = useState(0);
    const [batchPage, setBatchPage] = useState(0);
    const [busy, setBusy] = useState(false);

    const run = async (action: () => Promise<any>, onDone: (r: any) => void) => {
        setBusy(true);
        try {
            onDone(await action());
        } catch (e: any) {
            showMessage(e?.message ?? 'Request failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    const loadBatches = useCallback(async (page: number) => {
        try {
            const r = await fetchBatches(page, BATCH_PAGE_SIZE);
            setBatches(r?.batches ?? []);
            setBatchTotal(r?.totalElements ?? 0);
        } catch (e: any) {
            showMessage(e?.message ?? 'Could not load backfill batches', 'error');
        }
    }, []);

    useEffect(() => {
        loadBatches(batchPage);
    }, [batchPage, loadBatches]);

    const hubIds = selectedHubs.map((h) => h.id);
    const start = (r: BgvBatch) => {
        setFile(null);
        setFilePreview(null);
        showMessage(`Backfill batch #${r.batchId} queued`, 'success');
        setBatchPage(0);
        loadBatches(0);
    };

    return (
        <Box className={classes.backfill}>
            <Paper elevation={0} className={classes.panel} style={{ gridColumn: '1 / -1' }}>
                <Typography className={classes.panelTitle}>Start a backfill</Typography>
                <Typography className={classes.muted}>
                    Pick the stores, download their riders who ordered in the last 7 days (prefilled with the BGV
                    details), keep the riders to verify, correct father_name or address if needed, then upload it.
                </Typography>

                <Typography className={classes.stepTitle}>1. Select stores</Typography>
                <Autocomplete
                    multiple
                    options={hubs}
                    getOptionLabel={(h) => h.name}
                    value={selectedHubs}
                    onChange={(_, v) => setSelectedHubs(v)}
                    renderInput={(params) => <TextField {...params} variant="outlined" size="small" label="Hubs" />}
                />

                <Typography className={classes.stepTitle}>2. Download the rider sheet</Typography>
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<CloudDownloadIcon />}
                    disabled={busy || hubIds.length === 0}
                    onClick={() =>
                        run(() => fetchRiderSheet(hubIds), (r: { fileUrl: string; rowCount: number }) => {
                            if (!r?.rowCount) {
                                showMessage('No riders at these stores ordered in the last 7 days', 'info');
                                return;
                            }
                            window.open(r.fileUrl, '_blank');
                            showMessage(`Rider sheet ready: ${r.rowCount} riders`, 'success');
                        })
                    }
                >
                    Download rider sheet
                </Button>

                <Typography className={classes.stepTitle}>3. Upload the edited sheet</Typography>
                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                    <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>
                        Choose file
                        <input
                            type="file"
                            hidden
                            accept=".csv,.xlsx,.xls"
                            onChange={(e) => {
                                setFile(e.target.files?.[0] ?? null);
                                setFilePreview(null);
                                e.target.value = '';
                            }}
                        />
                    </Button>
                    <Typography className={classes.muted}>{file?.name ?? 'No file chosen'}</Typography>
                </Box>
                {filePreview && <PreviewStats preview={filePreview} />}
                {!canWrite && (
                    <Typography className={classes.muted} style={{ marginTop: 8 }}>
                        View only: you can download and preview the sheet, but not start a backfill.
                    </Typography>
                )}
                <Box mt={2} display="flex" style={{ gap: 8 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        disabled={busy || !file}
                        onClick={() => file && run(() => backfillByFile(file, false), setFilePreview)}
                    >
                        Preview
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!canWrite || busy || !filePreview || filePreview.toInitiate === 0}
                        onClick={() => file && run(() => backfillByFile(file, true), start)}
                    >
                        Start backfill
                    </Button>
                </Box>
            </Paper>

            <Paper elevation={0} className={classes.panel} style={{ gridColumn: '1 / -1' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography className={classes.panelTitle}>Recent batches</Typography>
                    <Button variant="outlined" size="small" startIcon={<RefreshIcon />} disabled={busy} onClick={() => loadBatches(batchPage)}>
                        Refresh
                    </Button>
                </Box>
                <TableContainer style={{ marginTop: 12 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {['Batch', 'File', 'Started', 'Status', 'Requested', 'Initiated', 'Skipped', 'Failed', ''].map((h) => (
                                    <TableCell key={h} className={classes.headCell}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {batches.map((b) => (
                                <TableRow key={b.batchId}>
                                    <TableCell className={classes.cell}>#{b.batchId}</TableCell>
                                    <TableCell className={classes.cell}>{b.fileName || '—'}</TableCell>
                                    <TableCell className={classes.cell}>{fmt(b.createdAt)}</TableCell>
                                    <TableCell className={classes.cell}>
                                        <Chip size="small" className={classes.chip} label={b.status} color={b.status === 'COMPLETED' ? 'primary' : 'default'} />
                                    </TableCell>
                                    <TableCell className={classes.cell}>{b.requested ?? 0}</TableCell>
                                    <TableCell className={classes.cell}>{b.initiated ?? 0}</TableCell>
                                    <TableCell className={classes.cell}>{b.skipped ?? 0}</TableCell>
                                    <TableCell className={classes.cell}>{b.failed ?? 0}</TableCell>
                                    <TableCell className={classes.cell}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            disabled={busy || b.status === 'RUNNING'}
                                            onClick={() =>
                                                run(() => fetchBatchReport(b.batchId), (r: BgvBatch) => {
                                                    if (r.reportUrl) window.open(r.reportUrl, '_blank');
                                                })
                                            }
                                        >
                                            Download report
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {batches.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" className={classes.muted} style={{ padding: 24 }}>
                                        No backfill batches yet
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={batchTotal}
                    page={batchPage}
                    rowsPerPage={BATCH_PAGE_SIZE}
                    rowsPerPageOptions={[BATCH_PAGE_SIZE]}
                    onChangePage={(_: any, p: number) => setBatchPage(p)}
                />
            </Paper>
        </Box>
    );
};

export default BgvTrackingPage;
